import React from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { makeStyles, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel } from '@material-ui/core';
import { descendingComparator, isNullOrUndefined, scoreToPercentage } from '../../helpers/Utils';
import { LoadingOverlay } from '../../components/Common/LoadingOverlay';
import { Alert } from '../../components/Common/Alert';
import ComplianceController from '../../controllers/ComplianceController';
import UserGroupController from '../../controllers/UserGroupController';

const useStyles = makeStyles(() => ({
    wrapper: {
        maxHeight: '100vh',
    },
    table: {
        maxHeight: '100vh',
        '& th': {
            fontWeight: 'bold',
        },
    },
}));

function OverviewUserGroups(props) {
    const { Auth, PushHistory } = props;
    const { shipId } = queryString.parse(props.location.search);
    const [userGroupStats, setUserGroupStats] = React.useState([]);
    const [userGroups, setUserGroups] = React.useState([]);
    const [redirectUrl, setRedirectUrl] = React.useState(null);
    const [warning, setWarning] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    //Sorting
    const [orderRow, setOrderRow] = React.useState('totalAnswered');
    const [orderDirection, setOrderDirection] = React.useState('desc');
    const classes = useStyles();

    const loadUserGroupOverview = React.useCallback(async () => {
        const response = await ComplianceController.getUserGroupsOverview(Auth.companyId, shipId);
        if (response.hasError) {
            setWarning(response.data);
        } else {
            setUserGroupStats(response.data);
        }
    }, [Auth, shipId]);

    const loadUserGroups = React.useCallback(async () => {
        const response = await UserGroupController.getUserGroups(Auth.companyId);
        if (response.hasError) {
            setWarning(response.data);
        } else {
            setUserGroups(response.data);
        }
    }, [Auth]);

    // init & auth check
    React.useEffect(() => {
        // ensure user is signed in
        const { isAuthenticated, isLoggingIn } = Auth;
        if (!isAuthenticated && !isLoggingIn) {
            setRedirectUrl('/Login');
        }
        // fetch ships for inital render
        async function init() {
            await Promise.all([loadUserGroupOverview(), loadUserGroups()]);
        }
        setLoading(true);
        init().then(() => setLoading(false));
    }, [Auth, loadUserGroupOverview, loadUserGroups]);

    // redirect
    React.useEffect(() => {
        if (!isNullOrUndefined(redirectUrl)) {
            PushHistory(redirectUrl);
        }
    }, [redirectUrl, PushHistory]);

    const handelSortChange = React.useCallback(
        newSort => {
            if (newSort === orderRow) {
                if (orderDirection === 'desc') {
                    setOrderDirection('asc');
                } else {
                    setOrderDirection('desc');
                }
                return;
            }
            setOrderRow(newSort);
            setOrderDirection('desc');
        },
        [orderRow, orderDirection, setOrderDirection, setOrderRow]
    );

    function getComparator(order, orderBy) {
        switch (orderBy) {
            case 'title':
                return order === 'desc' ? (a, b) => titleDescendingComparator(a, b) : (a, b) => -titleDescendingComparator(a, b);
            default:
                return order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
        }
    }

    function titleDescendingComparator(a, b) {
        const aComp = userGroups.find(e => e.id === a.title)?.name ?? 'No Name';
        const bComp = userGroups.find(e => e.id === b.title)?.name ?? 'No Name';
        if (bComp < aComp) {
            return -1;
        }
        if (bComp > aComp) {
            return 1;
        }
        return 0;
    }

    function buildTableHead() {
        return (
            <TableHead>
                <TableRow>
                    <TableCell>
                        <TableSortLabel active={orderRow === 'title'} direction={orderRow === 'title' ? orderDirection : 'desc'} onClick={() => handelSortChange('title')}>
                            Title
                        </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">
                        <TableSortLabel active={orderRow === 'totalAnswered'} direction={orderRow === 'totalAnswered' ? orderDirection : 'desc'} onClick={() => handelSortChange('totalAnswered')}>
                            Total Questions Answered
                        </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">
                        <TableSortLabel
                            active={orderRow === 'correctlyAnswered'}
                            direction={orderRow === 'correctlyAnswered' ? orderDirection : 'desc'}
                            onClick={() => handelSortChange('correctlyAnswered')}
                        >
                            Correct Answers
                        </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">
                        <TableSortLabel
                            active={orderRow === 'incorrectlyAnswered'}
                            direction={orderRow === 'incorrectlyAnswered' ? orderDirection : 'desc'}
                            onClick={() => handelSortChange('incorrectlyAnswered')}
                        >
                            Incorrect Answers
                        </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">
                        <TableSortLabel active={orderRow === 'totalMissed'} direction={orderRow === 'totalMissed' ? orderDirection : 'desc'} onClick={() => handelSortChange('totalMissed')}>
                            Missed Assessments
                        </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                        <TableSortLabel
                            active={orderRow === 'correctionPercentage'}
                            direction={orderRow === 'correctionPercentage' ? orderDirection : 'desc'}
                            onClick={() => handelSortChange('correctionPercentage')}
                        >
                            Avg. Score
                        </TableSortLabel>
                    </TableCell>
                </TableRow>
            </TableHead>
        );
    }

    function buildAssessmentRow(question) {
        const { id, title, totalAnswered, correctlyAnswered, incorrectlyAnswered, correctionPercentage, totalMissed } = question;
        return (
            <TableRow key={id}>
                <TableCell>{userGroups.find(e => e.id === title)?.name ?? 'No Name'}</TableCell>
                <TableCell align="center">{totalAnswered}</TableCell>
                <TableCell align="center">{correctlyAnswered}</TableCell>
                <TableCell align="center">{incorrectlyAnswered}</TableCell>
                <TableCell align="center">{totalMissed}</TableCell>
                <TableCell align="right">{scoreToPercentage(correctionPercentage, 1)}%</TableCell>
            </TableRow>
        );
    }

    function buildRows() {
        const comparator = getComparator(orderDirection, orderRow);
        return (
            userGroupStats
                ?.sort((a, b) => {
                    return comparator(a, b, orderRow);
                })
                .map(buildAssessmentRow) ?? <></>
        );
    }

    return (
        <div className={classes.wrapper}>
            <LoadingOverlay loading={loading} />
            <Alert text={warning} />
            <Paper square>
                <TableContainer className={classes.table}>
                    <Table stickyHeader size="small">
                        {buildTableHead()}
                        <TableBody>{buildRows()}</TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </div>
    );
}

const mapStateToProps = state => ({
    Auth: state.Authentication,
});

const mapDispatchToProps = dispatch => ({
    PushHistory: data => dispatch(push(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OverviewUserGroups);

OverviewUserGroups.propTypes = {
    Auth: PropTypes.object,
    PushHistory: PropTypes.func,
    location: PropTypes.object,
};
